import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UserX,
  Tag,
  Zap,
  ShieldCheck,
  Infinity,
  Globe,
  CheckCircle2,
  ArrowRight,
  FileAudio,
  Send,
  ClipboardCheck,
  Radio,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Partners from '../components/Partners';
import { rilisMusikFeatures, rilisMusikProcess, rilisMusikBenefits } from '../mock';

const iconMap = { UserX, Tag, Zap, ShieldCheck, Infinity, Globe };
const processIconMap = [FileAudio, Send, ClipboardCheck, Radio];

const RilisMusikPage = () => {
  useEffect(() => {
    document.title = 'Rilis Musik Gratis — Werdi Media';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-[#0a0a0a] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4A94F]/10 blur-[120px]" />
          <div className="absolute bottom-0 -right-32 w-[600px] h-[600px] rounded-full bg-[#B8892C]/8 blur-[130px]" />
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-medium tracking-wider uppercase text-[#E9C46A]">
              Rilis Musik — GRATIS Selamanya
            </span>
          </div>

          <h1 className="text-white font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6">
            Rilis Musikmu ke{' '}
            <span className="gold-gradient-text">Seluruh Dunia</span>
            <br />
            <span className="text-neutral-400 font-medium text-3xl md:text-4xl lg:text-5xl">
              Tanpa Biaya Sepeser Pun
            </span>
          </h1>

          <p className="text-neutral-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-10">
            Distribusikan lagu Anda ke <span className="text-white font-semibold">Spotify, Apple Music, JOOX, Deezer, TikTok, Facebook, Instagram</span>{' '}
            dan DSP worldwide lainnya secara GRATIS hanya di Werdi Media.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              asChild
              className="h-14 px-8 rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold text-base btn-transition gold-shimmer hover:shadow-[0_10px_40px_-10px_rgba(212,169,79,0.6)]"
            >
              <a href="https://www.werdimedia.com/submit-music" target="_blank" rel="noreferrer">
                Submit Music Sekarang <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefit ribbon */}
      <section className="relative py-10 border-y border-[#D4A94F]/15 bg-gradient-to-r from-[#0f0d09] via-[#0a0a0a] to-[#0f0d09]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {rilisMusikBenefits.map((b) => (
              <div key={b.label} className="text-center">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500 font-semibold mb-1">
                  {b.label}
                </p>
                <p className="text-white font-bold text-base md:text-lg">
                  <span className="gold-gradient-text">{b.value}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
              <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
                Feature
              </span>
            </div>
            <h2 className="text-white font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-5">
              Kenapa Rilis di{' '}
              <span className="gold-gradient-text">Werdi Media?</span>
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed">
              6 alasan kenapa 1.200+ artis Indonesia mempercayakan distribusi musiknya pada Werdi Media.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rilisMusikFeatures.map((f) => {
              const Icon = iconMap[f.icon];
              return (
                <div
                  key={f.title}
                  className="group relative bg-[#0f0f0f] border border-white/[0.06] hover:border-[#D4A94F]/40 rounded-3xl p-8 card-transition hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-[#D4A94F]/[0.06] blur-2xl group-hover:bg-[#D4A94F]/[0.12] btn-transition" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E9C46A]/20 to-[#B8892C]/10 border border-[#D4A94F]/30 flex items-center justify-center mb-5 group-hover:from-[#E9C46A] group-hover:to-[#D4A94F] btn-transition">
                      {Icon && <Icon className="w-5 h-5 text-[#E9C46A] group-hover:text-black btn-transition" strokeWidth={2} />}
                    </div>
                    <h3 className="text-white font-bold text-xl mb-3 tracking-tight">{f.title}</h3>
                    <p className="text-neutral-400 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="proses" className="relative py-24 lg:py-32 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
              <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
                Proses Distribusi
              </span>
            </div>
            <h2 className="text-white font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-5">
              4 Langkah Simpel &{' '}
              <span className="gold-gradient-text">Musikmu Live</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {rilisMusikProcess.map((p, i) => {
              const Icon = processIconMap[i];
              return (
                <div
                  key={p.step}
                  className="relative bg-[#0f0f0f] border border-white/[0.06] rounded-3xl p-8 hover:border-[#D4A94F]/40 card-transition"
                >
                  <div className="absolute -top-4 -left-4 w-14 h-14 rounded-full bg-gradient-to-br from-[#E9C46A] to-[#B8892C] flex items-center justify-center text-black font-bold text-lg shadow-[0_10px_30px_-5px_rgba(212,169,79,0.5)]">
                    {p.step}
                  </div>
                  <Icon className="w-8 h-8 text-[#D4A94F] mb-4 mt-6" />
                  <h3 className="text-white font-bold text-xl mb-3">{p.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="relative py-24 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
                <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
                  Requirement
                </span>
              </div>
              <h2 className="text-white font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-5">
                Persiapkan{' '}
                <span className="gold-gradient-text">Materi Musik</span>
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                Sebelum submit, pastikan Anda menyiapkan hal-hal berikut agar proses verifikasi berjalan cepat.
              </p>
              <ul className="space-y-4">
                {[
                  'File audio WAV atau FLAC (kualitas terbaik)',
                  'Sample rate 44.1 kHz atau 48 kHz',
                  'Bit depth 16-bit atau 24-bit',
                  'Cover Art minimal 1440×1440 pixel (bujur sangkar)',
                  'Judul lagu, nama artis, dan genre',
                  'Kredit komposer & penulis lirik (jika ada)',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-neutral-300">
                    <CheckCircle2 className="w-5 h-5 text-[#E9C46A] mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="rounded-3xl border border-[#D4A94F]/25 bg-gradient-to-br from-[#141210] via-[#0d0d0d] to-black p-8 shadow-2xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E9C46A] to-[#B8892C] flex items-center justify-center">
                    <FileAudio className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-white font-bold">Contoh Metadata</p>
                    <p className="text-xs text-neutral-500">Format yang direkomendasikan</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <MetaRow label="Judul Lagu" value="Rangkullah Hatiku" />
                  <MetaRow label="Artis Utama" value="Uchi Qolby" />
                  <MetaRow label="Genre Utama" value="Religi" />
                  <MetaRow label="Bahasa" value="Indonesia" />
                  <MetaRow label="Tanggal Rilis" value="20 April 2025" />
                  <MetaRow label="Label" value="Werdi Media" />
                  <MetaRow label="ISRC" value="Disediakan otomatis ✓" />
                  <MetaRow label="UPC" value="Disediakan otomatis ✓" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Partners />

      {/* CTA */}
      <section className="relative py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-[2.5rem] overflow-hidden border border-[#D4A94F]/25 bg-gradient-to-br from-[#141210] via-[#0d0d0d] to-black p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D4A94F]/15 blur-[80px]" />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#B8892C]/10 blur-[80px]" />
            <div className="relative">
              <h2 className="text-white font-bold text-3xl md:text-5xl leading-tight tracking-tight mb-4">
                Siap Rilis? <span className="gold-gradient-text">Klik di Bawah</span>
              </h2>
              <p className="text-neutral-400 text-lg mb-8">
                Distribusi gratis. Royalti 75% artis. Hak cipta 100% milik Anda.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button
                  asChild
                  className="h-14 px-8 rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold text-base btn-transition gold-shimmer"
                >
                  <a
                    href="https://www.werdimedia.com/submit-music"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Submit Music Sekarang <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="h-14 px-8 rounded-full bg-white/5 border border-white/15 hover:border-[#D4A94F] hover:bg-[#D4A94F]/10 text-white font-semibold btn-transition"
                >
                  <Link to="/kontak">Konsultasi Gratis</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const MetaRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/[0.05]">
    <span className="text-neutral-500">{label}</span>
    <span className="text-white font-medium">{value}</span>
  </div>
);

export default RilisMusikPage;
